import React, { useState, useEffect, useRef } from 'react';

export default function ReviewsToolbar({ value, onChange, availableTags = [], compact = false }) {
  const [local, setLocal] = useState(value);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const filterMenuRef = useRef(null);
  
  useEffect(() => { setLocal(value); }, [value]);
  useEffect(() => {
    const t = setTimeout(() => onChange(local), 300);
    return () => clearTimeout(t);
  }, [local, onChange]);

  // Close filter menu when clicking outside or scrolling
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (filterMenuRef.current && !filterMenuRef.current.contains(event.target)) {
        setIsFilterOpen(false);
      }
    };

    const handleScroll = () => {
      setIsFilterOpen(false);
    };

    if (isFilterOpen) {
      // Handle both mouse and touch events for better mobile support
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
      window.addEventListener('scroll', handleScroll, true);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        document.removeEventListener('touchstart', handleClickOutside);
        window.removeEventListener('scroll', handleScroll, true);
      };
    }
  }, [isFilterOpen]);

  const minRatingOptions = [null, 5, 4, 3, 2, 1];

  if (compact) {
    return (
      <div className="relative" ref={filterMenuRef}>
        <div className="flex items-center gap-2">
          <input
            type="text"
            placeholder="Search reviews"
            className="w-full px-4 py-2 rounded-full border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 text-sm"
            value={local.q || ''}
            onChange={(e) => setLocal({ ...local, q: e.target.value })}
          />
          <button
            className={`w-10 h-10 rounded-full border flex items-center justify-center text-gray-600 dark:text-gray-300 ${
              isFilterOpen 
                ? 'border-gray-400 dark:border-gray-600 bg-gray-100 dark:bg-gray-800' 
                : 'border-gray-300 dark:border-gray-700'
            }`}
            title="Filters"
            onClick={() => setIsFilterOpen(!isFilterOpen)}
          >
            <span className="sr-only">Filters</span>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="4" y1="21" x2="4" y2="14"></line><line x1="4" y1="10" x2="4" y2="3"></line><line x1="12" y1="21" x2="12" y2="12"></line><line x1="12" y1="8" x2="12" y2="3"></line><line x1="20" y1="21" x2="20" y2="16"></line><line x1="20" y1="12" x2="20" y2="3"></line><line x1="1" y1="14" x2="7" y2="14"></line><line x1="9" y1="8" x2="15" y2="8"></line><line x1="17" y1="16" x2="23" y2="16"></line></svg>
          </button>
        </div>
        
        {isFilterOpen && (
          <div className="absolute top-full right-0 mt-2 w-[calc(100vw-2rem)] max-w-64 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-700 rounded-lg shadow-lg p-4 z-50">
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Sort by</label>
                <select
                  className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 text-sm dark:text-gray-300"
                  value={local.sort || 'recent'}
                  onChange={(e) => {
                    setLocal({ ...local, sort: e.target.value });
                    setIsFilterOpen(false);
                  }}
                >
                  <option value="recent">Most recent</option>
                  <option value="rating_high">Highest rating</option>
                  <option value="rating_low">Lowest rating</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Minimum rating</label>
                <div className="flex items-center gap-2 flex-wrap">
                  {minRatingOptions.map((r, i) => (
                    <button
                      key={i}
                      onClick={() => {
                        setLocal({ ...local, minRating: r });
                        setIsFilterOpen(false);
                      }}
                      className={`px-2 py-1 rounded-full text-xs border ${
                        local.minRating === r 
                          ? 'bg-yellow-500 dark:bg-yellow-400 text-black dark:text-black border-yellow-500 dark:border-yellow-400' 
                          : 'border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:border-gray-400 dark:hover:border-gray-600'
                      }`}
                    >
                      {r ? `${r}+` : 'All'}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col md:flex-row md:items-center gap-3">
      <input
        type="text"
        placeholder="Search reviews"
        className="w-full md:w-64 px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 text-sm dark:text-gray-300"
        value={local.q || ''}
        onChange={(e) => setLocal({ ...local, q: e.target.value })}
      />

      <select
        className="px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 text-sm dark:text-gray-300"
        value={local.sort || 'recent'}
        onChange={(e) => setLocal({ ...local, sort: e.target.value })}
      >
        <option value="recent">Most recent</option>
        <option value="rating_high">Highest rating</option>
        <option value="rating_low">Lowest rating</option>
      </select>

      <div className="flex items-center gap-2 flex-wrap">
        {minRatingOptions.map((r, i) => (
          <button
            key={i}
            onClick={() => setLocal({ ...local, minRating: r })}
            className={`px-2 py-1 rounded-full text-xs border ${local.minRating === r ? 'bg-yellow-500 dark:bg-yellow-400 text-black dark:text-black border-yellow-500 dark:border-yellow-400' : 'border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:border-gray-400 dark:hover:border-gray-600'}`}
          >
            {r ? `${r}+` : 'All'}
          </button>
        ))}
      </div>
    </div>
  );
}


